import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, ClipboardCheck, Lightbulb, Building2, Users, Scale, MessageCircle } from "lucide-react";
import { SERVICES, SERVICE_FILTERS, waLink } from "../data/content";
import { fadeUp, viewportOnce, EYEBROW } from "../lib/motion";

const ICONS = { FileText, ClipboardCheck, Lightbulb, Building2, Users, Scale };

export const Services = () => {
  const [active, setActive] = useState("all");
  const visible = active === "all" ? SERVICES : SERVICES.filter((s) => s.id === active);

  return (
    <section id="layanan" data-testid="services-section" className="relative bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl mx-auto text-center"
        >
          <span className={EYEBROW}>Layanan Kami</span>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight text-navy leading-[1.05] font-bold">
            Enam Bidang Layanan <span className="italic text-gold-dark">Hukum Terpadu</span>
          </h2>
          <p className="mt-5 text-graytone-dark text-lg">
            Pilih kategori untuk menyaring layanan sesuai kebutuhan Anda.
          </p>
        </motion.div>

        {/* Filter tabs */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-10 flex flex-wrap justify-center gap-2.5"
          data-testid="service-filters"
        >
          {SERVICE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setActive(f.id)}
              data-testid={`filter-${f.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all duration-300 ${
                active === f.id
                  ? "bg-navy text-white border-navy shadow-soft"
                  : "bg-white text-navy/70 border-graytone-medium hover:border-gold hover:text-navy"
              }`}
            >
              {f.label}
            </button>
          ))}
        </motion.div>

        {/* Cards */}
        <motion.div layout className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <motion.article
                  key={s.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  data-testid={`service-card-${s.id}`}
                  className="group flex flex-col rounded-3xl bg-graytone-light border border-graytone-medium/50 p-8 shadow-soft hover:shadow-medium hover:-translate-y-2 transition-all duration-400"
                >
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-navy text-gold group-hover:bg-gold group-hover:text-navy transition-colors duration-300">
                    <Icon className="h-7 w-7" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-semibold text-navy">{s.title}</h3>
                  <div className="mt-5 flex flex-wrap gap-2 flex-1 content-start">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-white border border-graytone-medium/70 px-3.5 py-1.5 text-xs font-medium text-navy/75"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <a
                    href={waLink(`Halo FYLegal.ID, saya ingin konsultasi terkait layanan ${s.title}.`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`service-cta-${s.id}`}
                    className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-5 py-3 text-sm font-semibold hover:bg-gold hover:text-navy transition-all duration-300 w-fit"
                  >
                    <MessageCircle className="h-4 w-4" /> Hubungi Kami
                  </a>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
